import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, from, map, of, switchMap } from 'rxjs';

import { OneSignalService } from '@ninety/platform/services/services/onesignal.service';
import { PersonService } from '@ninety/platform/services/services/person.service';

import { LogOutActions } from '../auth/logout/logout.actions';
import { NotificationActions } from '../notifications/notification.actions';
import { selectNotificationsEnabled } from '../notifications/notification.selectors';

import { CurrentPersonStateActions } from './current-person.actions';

@Injectable()
export class CurrentPersonEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly personService = inject(PersonService);
  private readonly oneSignal = inject(OneSignalService);

  updateCurrentPerson$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CurrentPersonStateActions.updateCurrentPerson),
      switchMap(({ update }) =>
        this.personService.updatePerson(update).pipe(
          map(() => CurrentPersonStateActions.updateCurrentPersonSuccess({ update })),
          catchError((error: unknown) => of(NotificationActions.notifyError({ error, message: 'Failed to update.' })))
        )
      )
    )
  );

  ensureOneSignalLinked$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CurrentPersonStateActions.setPerson, CurrentPersonStateActions.loginUser),
        concatLatestFrom(() => [this.store.select(selectNotificationsEnabled)]),
        switchMap(([{ person }, enabled]) => {
          if (!enabled || !person?._id) return of(void 0);
          return from(
            this.oneSignal.loginUser(person._id, person.legals?.privacyPolicy, person.notificationBackoff)
          ).pipe(catchError(() => of(void 0)));
        })
      ),
    { dispatch: false }
  );

  appLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LogOutActions.initiateLogOut),
        concatLatestFrom(() => this.store.select(selectNotificationsEnabled)),
        switchMap(([, enabled]) => {
          if (!enabled) return of(void 0);
          return from(this.oneSignal.logOutUser()).pipe(catchError(() => of(void 0)));
        })
      ),
    { dispatch: false }
  );
}
